/*
 * Jira Ticket:
 * Created Date: Wed, 11th Mar 2020, 15:04:35 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import { dynamicInviteRoleOptions } from 'utils/choices';

const super_admin = 'SUPER_ADMIN';
const super_manager = 'SUPER_MANAGER';
const manager = 'MANAGER';
const project_manager = 'PROJECT_MANAGER';
const approver = 'APPROVER';
const tower = 'BUILDER';

const rolesArray = [
  { id: super_admin, name: 'Super Admin' },
  { id: super_manager, name: 'Super Manager' },
  { id: manager, name: 'Manager' },
  { id: project_manager, name: 'Project Manager' },
  { id: approver, name: 'Approver' },
  { id: tower, name: 'App User' },
];

const reducedRolesArray = [
  { id: super_manager, name: 'Super Manager' },
  { id: manager, name: 'Manager' },
  { id: project_manager, name: 'Project Manager' },
  { id: approver, name: 'Approver' },
  { id: tower, name: 'App User' },
];

const rolesTextArray = [
  super_admin,
  super_manager,
  manager,
  project_manager,
  approver,
  tower,
];

const roleAtLeastSuperManager = role =>
  role === super_admin || role === super_manager;

const roleIsSuperManager = role => role === super_manager;

const roleAtLeastManager = role =>
  role === super_admin || role === super_manager || role === manager;

const roleIsManager = role => role === manager;

const roleAtLeastProjectManager = role =>
  role === super_admin ||
  role === super_manager ||
  role === manager ||
  role === project_manager;
const roleIsProjectManager = role => role === project_manager;

const roleAtLeastApprover = role =>
  role === super_admin ||
  role === super_manager ||
  role === manager ||
  role === project_manager ||
  role === approver;
const roleIsApprover = role => role === approver;

const roleAtLeastBuilder = role =>
  role === super_admin ||
  role === super_manager ||
  role === manager ||
  role === project_manager ||
  role === approver ||
  role === tower;
const roleIsBuiler = role => role === tower;

const getMaximumRole = (permissions, orgId = null) => {
  if (permissions && permissions.length > 0) {
    const userIsSuperAdmin = isSuperAdmin(permissions);
    const userIsSuperManager = permissions.some(userRole => {
      if (orgId) {
        return (
          userRole.organisationId === orgId && roleIsSuperManager(userRole.role)
        );
      }
      return roleIsSuperManager(userRole.role);
    });
    const userIsManager = permissions.some(userRole => {
      if (orgId) {
        return (
          userRole.organisationId === orgId && roleIsManager(userRole.role)
        );
      }
      return roleIsManager(userRole.role);
    });
    const userIsProjectManager = permissions.some(userRole => {
      if (orgId) {
        return (
          userRole.organisationId === orgId &&
          roleIsProjectManager(userRole.role)
        );
      }
      return roleIsProjectManager(userRole.role);
    });
    const userIsApprover = permissions.some(userRole => {
      if (orgId) {
        return (
          userRole.organisationId === orgId && roleIsApprover(userRole.role)
        );
      }
      return roleIsApprover(userRole.role);
    });
    const userIsBuilder = permissions.some(userRole => {
      if (orgId) {
        return userRole.organisationId === orgId && roleIsBuiler(userRole.role);
      }
      return roleIsBuiler(userRole.role);
    });

    if (userIsSuperAdmin) {
      return super_admin;
    } else if (userIsSuperManager) {
      return super_manager;
    } else if (userIsManager) {
      return manager;
    } else if (userIsProjectManager) {
      return project_manager;
    } else if (userIsApprover) {
      return approver;
    } else if (userIsBuilder) {
      return tower;
    } else {
      return null;
    }
  }
  return null;
};

const isSuperAdmin = permissions => {
  if (permissions && permissions.length > 0) {
    return permissions.some(userRole => userRole.role === super_admin);
  }
  return false;
};

const isSuperManager = (permissions, orgId = null) => {
  if (permissions && permissions.length > 0) {
    if (orgId) {
      return permissions.some(
        userRole =>
          userRole.role === super_admin ||
          (userRole.organisationId === orgId &&
            roleAtLeastSuperManager(userRole.role))
      );
    }
    return permissions.some(userRole => roleAtLeastSuperManager(userRole.role));
  }
  return false;
};

const isManager = (permissions, orgId = null) => {
  if (permissions && permissions.length > 0) {
    if (orgId) {
      return permissions.some(
        userRole =>
          userRole.role === super_admin ||
          (userRole.organisationId === orgId &&
            roleAtLeastManager(userRole.role))
      );
    }
    return permissions.some(userRole => roleAtLeastManager(userRole.role));
  }
  return false;
};

const isProjectManager = (permissions, orgId = null) => {
  if (permissions && permissions.length > 0) {
    if (orgId) {
      return permissions.some(
        userRole =>
          userRole.role === super_admin ||
          (userRole.organisationId === orgId &&
            roleAtLeastProjectManager(userRole.role))
      );
    }
    return permissions.some(userRole =>
      roleAtLeastProjectManager(userRole.role)
    );
  }
  return false;
};

const isApprover = (permissions, orgId = null) => {
  if (permissions && permissions.length > 0) {
    if (orgId) {
      return permissions.some(
        userRole =>
          userRole.role === super_admin ||
          (userRole.organisationId === orgId &&
            roleAtLeastApprover(userRole.role))
      );
    }
    return permissions.some(userRole => roleAtLeastApprover(userRole.role));
  }
  return false;
};

const isBuilder = (permissions, orgId = null) => {
  if (permissions && permissions.length > 0) {
    if (orgId) {
      return permissions.some(
        userRole =>
          userRole.role === super_admin ||
          (userRole.organisationId === orgId &&
            roleAtLeastBuilder(userRole.role))
      );
    }
    return permissions.some(userRole => roleAtLeastBuilder(userRole.role));
  }
  return false;
};

const isSelf = userId => {
  const currentUserId = localStorage.getItem('userSub');
  return currentUserId === userId;
};

const userIsWithinRole = ({ userRole, maxRoleLevel }) => {
  const allowedRoles = dynamicInviteRoleOptions(maxRoleLevel);
  return allowedRoles.some(role => role.id === userRole);
};

export {
  super_admin,
  super_manager,
  manager,
  project_manager,
  approver,
  tower,
  isSuperAdmin,
  isSuperManager,
  isManager,
  isProjectManager,
  isApprover,
  isBuilder,
  isSelf,
  rolesArray,
  reducedRolesArray,
  rolesTextArray,
  getMaximumRole,
  userIsWithinRole,
};
